import { BackButton } from "@/components/BackButton";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Shuffle, Check, RotateCcw, Lock, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { BEGINNER_TERMS, ADVANCED_TERMS } from "@/lib/theory";
import { ALL_LESSONS, getLessonContext } from "@/data/curriculum";
import { TermsQuiz } from "@/components/TermsQuiz";

const learnedGroups = (completed) => {
  const seen = new Set();
  return ALL_LESSONS.filter((l) => completed.has(l.id)).map((l) => {
    const ctx = getLessonContext(l.id);
    const terms = l.sections.filter((x) => x.terms).flatMap((x) => x.terms)
      .filter(([, term]) => !seen.has(`${ctx?.instrument.id}:${term}`) && seen.add(`${ctx?.instrument.id}:${term}`)).map(([sign, term, meaning]) => ({ sign, term, meaning }));
    return { id: l.id, label: ctx ? `${ctx.instrument.label} · Lesson ${ctx.number}: ${l.title}` : l.title, terms };
  }).filter((g) => g.terms.length);
};

const LearnedList = ({ groups }) => (
  <div className="mt-10 space-y-5" data-testid="terms-learned-list">
    <h2 className="font-serif text-2xl">My musical terms</h2>
    {groups.length === 0 && <p className="text-sm text-muted-foreground" data-testid="terms-learned-empty">Complete a lesson with musical terms and they will appear here, lesson by lesson.</p>}
    {groups.map((g) => (
      <div key={g.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`terms-group-${g.id}`}>
        <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{g.label}</p>
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {g.terms.map((t) => <li key={t.term} className="text-sm"><span className="font-serif italic text-primary">{t.sign}</span> <strong>{t.term}</strong> <span className="text-muted-foreground">— {t.meaning}</span></li>)}
        </ul>
      </div>
    ))}
  </div>
);

const KEY = "jt_terms_known";
const loadKnown = () => { try { return new Set(JSON.parse(localStorage.getItem(KEY) || "[]")); } catch (e) { return new Set(); } };

const Card = ({ card, flipped, onFlip }) => (
  <button onClick={onFlip} data-testid="flashcard" className="relative h-64 w-full [perspective:1200px]" aria-label="Flip card">
    <div className={`absolute inset-0 rounded-3xl transition-transform duration-500 [transform-style:preserve-3d] ${flipped ? "[transform:rotateY(180deg)]" : ""}`}>
      <div className="absolute inset-0 flex flex-col items-center justify-center rounded-3xl border border-border bg-card p-6 [backface-visibility:hidden]">
        <p className="font-serif text-6xl italic text-primary" data-testid="flashcard-sign">{card.sign}</p>
        <p className="mt-4 text-xs text-muted-foreground">Tap to see the meaning</p>
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center rounded-3xl border border-primary/50 bg-primary/10 p-6 [backface-visibility:hidden] [transform:rotateY(180deg)]">
        <p className="font-serif text-2xl italic text-foreground">{card.term}</p>
        <p className="mt-2 text-lg text-foreground" data-testid="flashcard-meaning">{card.meaning}</p>
      </div>
    </div>
  </button>
);

export default function TermsTrainer() {
  const { user, purchases, progress } = useAuth();
  const groups = useMemo(() => learnedGroups(new Set(progress?.completed_lessons || [])), [progress]);
  const mine = useMemo(() => [...new Map(groups.flatMap((g) => g.terms).map((t) => [t.term, t])).values()], [groups]);
  const [level, setLevel] = useState("mine");
  const [seed, setSeed] = useState(0);
  const [known, setKnown] = useState(loadKnown);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const all = level === "mine" ? mine : level === "beginner" ? BEGINNER_TERMS : ADVANCED_TERMS;
  const deck = useMemo(() => {
    const d = all.filter((t) => !known.has(t.term));
    return seed ? [...d].sort(() => Math.random() - 0.5) : d;
  }, [all, known, seed]); // eslint-disable-line react-hooks/exhaustive-deps
  const owned = user && (user.role === "admin" || (purchases || []).length > 0);
  if (!owned) return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="terms-locked">
      <BackButton testId="terms-locked-back-button" fallback="/courses" />
      <Lock className="mx-auto mt-6 text-primary" /><h1 className="mt-4 font-serif text-3xl">Music terms flashcards</h1>
      <p className="mt-2 text-muted-foreground">Included with every course.</p>
      <Link to="/courses"><Button className="mt-6 rounded-full">Browse courses</Button></Link>
    </div>
  );
  const save = (s) => { setKnown(s); try { localStorage.setItem(KEY, JSON.stringify([...s])); } catch (e) { /* storage blocked */ } };
  const card = deck[idx % Math.max(deck.length, 1)];
  const next = () => { setFlipped(false); setIdx((i) => i + 1); };
  const knew = () => { const s = new Set(known); s.add(card.term); save(s); setFlipped(false); };
  const done = all.filter((t) => known.has(t.term)).length;
  return (
    <div className="mx-auto max-w-xl px-6 pb-24 pt-10" data-testid="terms-trainer">
      <div className="mb-5"><BackButton testId="terms-back-button" fallback="/dashboard" /></div>
      <Logo height={56} />
      <h1 className="mt-6 font-serif text-4xl tracking-tight sm:text-5xl">Music terms</h1>
      <p className="mt-2 text-muted-foreground">Tap a card to flip it. Sort it into "I knew it" or "Again". "From my lessons" grows with every lesson you complete — learn each lesson's terms, then add the new ones next lesson.</p>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {[["mine", "From my lessons"], ["beginner", "Beginner"], ["advanced", "Advanced"]].map(([k, l]) => (
          <button key={k} onClick={() => { setLevel(k); setIdx(0); setFlipped(false); }} data-testid={`terms-level-${k}`}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${level === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{l}</button>
        ))}
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setSeed((x) => x + 1); setIdx(0); setFlipped(false); }} data-testid="terms-shuffle"><Shuffle size={14} className="mr-1.5" /> Shuffle</Button>
        <span className="ml-auto flex items-center gap-1.5 font-mono text-xs text-muted-foreground" data-testid="terms-progress"><Layers size={13} /> {done}/{all.length} known</span>
      </div>
      <div className="mt-6">
        {card ? <Card card={card} flipped={flipped} onFlip={() => setFlipped((f) => !f)} /> : (
          <div className="flex h-64 flex-col items-center justify-center rounded-3xl border border-dashed border-primary/50 text-center" data-testid="terms-complete">
            <p className="font-serif text-2xl">{level === "mine" && !mine.length ? "No terms yet — complete a lesson to add some." : "You know every term in this deck!"}</p>
          </div>
        )}
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        {card && <Button onClick={knew} className="rounded-full" data-testid="terms-knew"><Check size={15} className="mr-1.5" /> I knew it</Button>}
        {card && <Button variant="outline" onClick={next} className="rounded-full" data-testid="terms-again">Again</Button>}
        <Button variant="ghost" onClick={() => { const s = new Set([...known].filter((t) => !all.some((a) => a.term === t))); save(s); setIdx(0); }} className="rounded-full" data-testid="terms-reset"><RotateCcw size={14} className="mr-1.5" /> Reset {level}</Button>
      </div>
      <TermsQuiz deck={mine.length >= 4 ? mine : BEGINNER_TERMS} />
      <LearnedList groups={groups} />
    </div>
  );
}
